import { ofetch } from 'ofetch';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'wouter';
import { backend, useBackendMutation } from '@/backend';
import type { WorkspaceType } from '@/base/constants';
import { BASE, WORKSPACE } from '@/base/constants';
import { useLocation } from '@/base/router';
import { createEventBus } from '@/shared/js';
import { useRuntimeEnv } from '@/shared/runtimeEnv';

export const createAuthNextPath = (workspace: WorkspaceType, path: string) => {
  return btoa(`${workspace}:${path}`);
};

export const parseAuthNextPath = (
  next?: string,
): { workspace: WorkspaceType; path: string } => {
  try {
    const raw = atob(next ?? '');
    if (!raw.includes(':'))
      return {
        workspace: 'public',
        path: '/',
      };
    const [ws, path] = raw.split(':');
    return {
      workspace: ws as WorkspaceType,
      path,
    };
  } catch (_) {
    return {
      workspace: 'public',
      path: '/',
    };
  }
};

export const redirectToLogin = () => {
  let currentPath = window.location.pathname;
  if (BASE && currentPath.startsWith(`/${BASE}`)) {
    currentPath = currentPath.slice(BASE.length + 1);
  }
  if (WORKSPACE !== 'public' && currentPath.startsWith(`/${WORKSPACE}`)) {
    currentPath = currentPath.slice(WORKSPACE.length + 1);
  }
  const next = createAuthNextPath(WORKSPACE, currentPath);
  window.location.href = `${location.origin}/${BASE ? `/${BASE}` : ''}/app/auth?next=${next}`;
};

export const useReferralCode = () => {
  const [searchParams] = useSearchParams();
  return searchParams.get('ref') || undefined;
};

const tokenBus = createEventBus<string>();

export const getAccessToken = () => localStorage.getItem('access-token');
export const setAccessToken = (newToken?: string) => {
  const newVal = newToken
    ? localStorage.setItem('access-token', newToken)
    : localStorage.removeItem('access-token');
  tokenBus.dispatch(newToken);
  return newVal;
};
export const hasAccessToken = () => !!getAccessToken();

export const useHasAccessToken = () => {
  const [value, setValue] = useState(hasAccessToken());
  useEffect(() => {
    const unsubscribe = tokenBus.subscribe(() => setValue(hasAccessToken()));
    return () => unsubscribe();
  });
  return value;
};

type AccessTokenContent = {
  exp: number;
  iat: number;
  username: string;
  admin: null | {
    is_superadmin: boolean;
    roles: string[];
  };
};

export const useParsedAccessToken = () => {
  const [value, setValue] = useState(getAccessToken());
  useEffect(() => {
    const unsubscribe = tokenBus.subscribe(() => setValue(getAccessToken()));
    return () => unsubscribe();
  }, []);
  try {
    return value
      ? (JSON.parse(atob(value.split('.')[1])) as AccessTokenContent)
      : null;
  } catch {
    return null;
  }
};

let accessTokenPromise: Promise<string> | undefined;
export const refreshAccessToken = () => {
  if (!accessTokenPromise) {
    accessTokenPromise = backend(
      'account:post:auth/access-token/',
      {},
      {
        credentials: 'include',
        body: {},
        method: 'POST',
        onResponseError: [
          ({ request, response, options, error }) => {
            if ([401, 403].includes(response?.status as never)) throw error;
            return ofetch(request, options);
          },
        ],
        retry: 999,
        retryDelay: 3000,
      },
    )
      .then((data) => data.response.access_token)
      .catch(() => '')
      .finally(() => {
        accessTokenPromise = undefined;
      });
  }
  return accessTokenPromise;
};

export const useRequestLogin = () =>
  useBackendMutation('account:post:auth/email-or-phone-login/', {
    fetchOptions: {
      credentials: 'include',
    },
    recaptchaAction: 'login',
  });

export const useVerifyLogin = () => {
  const referralCode = useReferralCode();
  const { launchSource: through } = useRuntimeEnv();

  return useBackendMutation('account:post:auth/verify-otp/', {
    fetchOptions: {
      credentials: 'include',
      onRequest: (req) => {
        if (typeof req.options.body === 'object') {
          req.options.body = {
            ...(req.options.body ?? {}),
            referrer_code: referralCode || undefined,
            through: through || undefined,
          };
        }
      },
      onResponse: async () => {
        const accessToken = await refreshAccessToken();
        setAccessToken(accessToken);
      },
    },
    revalidate: ['*'],
  });
};

export const useVerifyGoogleLogin = () => {
  const referralCode = useReferralCode();
  const { launchSource: through } = useRuntimeEnv();

  return useBackendMutation('account:post:auth/google-login/', {
    fetchOptions: {
      credentials: 'include',
      onRequest: (req) => {
        if (typeof req.options.body === 'object') {
          req.options.body = {
            ...(req.options.body ?? {}),
            referrer_code: referralCode || undefined,
            through: through || undefined,
          };
        }
      },
      onResponse: async () => {
        const accessToken = await refreshAccessToken();
        setAccessToken(accessToken);
      },
    },
    revalidate: ['*'],
  });
};

export const useLogout = (redirect?: string) => {
  const [, navigate] = useLocation();
  return useBackendMutation('account:post:auth/logout/', {
    fetchOptions: {
      credentials: 'include',
    },
    swrOptions: {
      onSuccess: () => {
        setAccessToken(undefined);
        redirect && navigate('public', '/');
      },
    },
  });
};
